import { AddTask, CardTravel, Dns, Web } from '@mui/icons-material';
import MenuIcon from '@mui/icons-material/Menu';
import {
  AppBar,
  Box,
  Button,
  Divider,
  Drawer,
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  Toolbar,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import packageJson from '../../package.json';
import { getAbout } from '../http/AboutApi';
import AuthIndicatorComponent from './auth/AuthIndicatorComponent';
import LeftMenu from './LeftMenu';

const SPACING = {
  XS: 1,
  SM: 2,
  MD: 3,
  LG: 4,
};

// Injected at build time from the release tag (see Dockerfile / ci.yml).
// Falls back to package.json version for local development.
const FRONTEND_VERSION = process.env.REACT_APP_VERSION || packageJson.version;

const VERSION_ICON_SX = { fontSize: 14 };

function VersionItem({ icon, value }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      {icon}
      <Typography variant="caption" noWrap component="span" lineHeight={1.2}>
        {value}
      </Typography>
    </Box>
  );
}

function VersionInfo({ frontendVersion, backendVersion }) {
  if (!frontendVersion && !backendVersion) return null;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'row', md: 'column' },
        alignItems: { xs: 'center', md: 'flex-end' },
        gap: { xs: SPACING.SM, md: 0.25 },
        opacity: 0.7,
      }}
    >
      {backendVersion && (
        <VersionItem
          icon={<Dns sx={VERSION_ICON_SX} />}
          value={backendVersion}
        />
      )}
      {frontendVersion && (
        <VersionItem
          icon={<Web sx={VERSION_ICON_SX} />}
          value={frontendVersion}
        />
      )}
    </Box>
  );
}

const NAV_LINKS = [
  { label: 'Companies', to: '/companies' },
  { label: 'Tokens', to: '/tokens' },
  { label: 'Wallets', to: '/wallets' },
];

export default function Header({ portfolios, setAddPortfolioDialogOpen }) {
  const { pathname } = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [portfoliosAnchor, setPortfoliosAnchor] = useState(null);
  const [version, setVersion] = useState(null);

  const closePortfoliosMenu = () => setPortfoliosAnchor(null);

  useEffect(() => {
    getAbout()
      .then(({ data }) => setVersion(data.version))
      .catch(() => setVersion(null));
  }, []);

  return (
    <>
      <AppBar position="sticky">
        <Toolbar sx={{ gap: SPACING.SM }}>
          <IconButton
            color="inherit"
            edge="start"
            aria-label="open navigation"
            onClick={() => setMobileOpen(true)}
            sx={{ display: { xs: 'inline-flex', md: 'none' } }}
          >
            <MenuIcon />
          </IconButton>

          <Typography variant="h6" noWrap component="div">
            Financial Advisor
          </Typography>

          <Box
            sx={{
              flexGrow: 1,
              display: { xs: 'none', md: 'flex' },
              gap: SPACING.XS,
              ml: SPACING.MD,
            }}
          >
            <Button
              color="inherit"
              onClick={(event) => setPortfoliosAnchor(event.currentTarget)}
              sx={{
                fontWeight: pathname.startsWith('/portfolios') ? 700 : 400,
              }}
            >
              Portfolios
            </Button>
            <Menu
              anchorEl={portfoliosAnchor}
              open={Boolean(portfoliosAnchor)}
              onClose={closePortfoliosMenu}
            >
              {portfolios.map((portfolio) => (
                <MenuItem
                  key={portfolio.id}
                  component={Link}
                  to={`/portfolios/${portfolio.id}`}
                  selected={pathname === `/portfolios/${portfolio.id}`}
                  onClick={closePortfoliosMenu}
                >
                  <ListItemIcon>
                    <CardTravel fontSize="small" />
                  </ListItemIcon>
                  {portfolio.name}
                </MenuItem>
              ))}
              {portfolios.length > 0 && <Divider />}
              <MenuItem
                onClick={() => {
                  setAddPortfolioDialogOpen(true);
                  closePortfoliosMenu();
                }}
              >
                <ListItemIcon>
                  <AddTask fontSize="small" />
                </ListItemIcon>
                New
              </MenuItem>
            </Menu>
            {NAV_LINKS.map(({ label, to }) => (
              <Button
                key={to}
                component={Link}
                to={to}
                color="inherit"
                sx={{
                  fontWeight: pathname === to ? 700 : 400,
                }}
              >
                {label}
              </Button>
            ))}
          </Box>

          <Box sx={{ flexGrow: { xs: 1, md: 0 } }} />
          <VersionInfo frontendVersion={FRONTEND_VERSION} backendVersion={version} />
          <AuthIndicatorComponent />
        </Toolbar>
      </AppBar>

      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        ModalProps={{ keepMounted: true }}
        sx={{ display: { xs: 'block', md: 'none' } }}
      >
        <LeftMenu
          portfolios={portfolios}
          setAddPortfolioDialogOpen={setAddPortfolioDialogOpen}
          onNavigate={() => setMobileOpen(false)}
        />
      </Drawer>
    </>
  );
}
